import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { ArrowLeft, Download, Printer } from "lucide-react";
import moment from "moment";
import React, { useEffect, useRef, useState } from "react";

function PrintWrapper({ children, title = "", onBack }) {
  const printRef = useRef();
  const [downloading, setDownloading] = useState(false);
  const [MasterCompanyDetails, setMasterCompanyDetails] = useState(false);
  useEffect(() => {
    const stored = localStorage.getItem("MASTER_COMPANY_DETAILS");
    if (stored) {
      setMasterCompanyDetails(JSON.parse(stored));
    }
  }, []);

  const handlePrint = () => {
    const printContent = printRef.current.innerHTML;

    // Create hidden iframe
    const iframe = document.createElement("iframe");
    iframe.style.position = "fixed";
    iframe.style.right = "0";
    iframe.style.bottom = "0";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "0";

    document.body.appendChild(iframe);

    const iframeDoc = iframe.contentWindow.document;

    iframeDoc.open();
    iframeDoc.write(`
        <!DOCTYPE html>
        <html>
        <head>
            <title>${title}</title>
            <meta charset="UTF-8" />
            <meta name="viewport" content="width=device-width, initial-scale=1.0" />
            <script src="https://cdn.tailwindcss.com"></script>
            <style>
                @page {
                    size: A4;
                    margin: 15mm;
                }

                body {
                    margin: 0;
                    padding: 0;
                    -webkit-print-color-adjust: exact;
                    print-color-adjust: exact;
                }
            </style>
        </head>
        <body class="bg-white">
            ${printContent}
        </body>
        </html>
    `);
    iframeDoc.close();

    // Print after content loads
    iframe.onload = () => {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();

      // Cleanup after print
      iframe.contentWindow.onafterprint = () => {
        document.body.removeChild(iframe);
      };
    };
  };

  const handleDownload = async () => {
    if (!printRef.current) return;

    try {
      setDownloading(true);
      const canvas = await html2canvas(printRef.current, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: "#ffffff",
      });

      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
      });

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      const ratio = imgWidth / imgHeight;

      let width = pageWidth - 20;
      let height = width / ratio;

      if (height > pageHeight - 20) {
        height = pageHeight - 20;
        width = height * ratio;
      }

      const x = (pageWidth - width) / 2;
      const y = (pageHeight - height) / 2;

      pdf.addImage(imgData, "PNG", x, y, width, height);
      pdf.save(`${title || "Document"}_${moment().format("YYYY-MM-DD")}.pdf`);
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert("Failed to generate PDF. Please try printing instead.");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <>
      <div className="min-h-screen bg-gray-200 print:bg-white">
        {/* Action Bar */}
        <div className="print:hidden bg-white shadow-sm border-b border-gray-300 sticky top-0 z-10">
          <div
            className={`max-w-4xl mx-auto px-3 sm:px-4 py-2 sm:py-3 flex flex-col sm:flex-row items-center gap-2 sm:gap-0 ${onBack ? "sm:justify-between" : "sm:justify-center"}`}
          >
            {typeof onBack === "function" && (
              <button
                onClick={onBack}
                className="w-full sm:w-auto flex items-center justify-center sm:justify-start gap-2 px-4 py-2 text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-all font-medium"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Booking
              </button>
            )}
            <div className="flex items-center gap-2 sm:gap-3 w-full sm:w-auto">
              <button
                onClick={handleDownload}
                data-download-btn
                disabled={downloading}
                className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-3 sm:px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-all font-medium border border-gray-300 text-sm sm:text-base"
              >
                <Download className="w-4 h-4" />
                {downloading ? "Generating..." : "Download"}
              </button>
              <button
                onClick={handlePrint}
                className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 sm:px-5 py-2 bg-[#1a2744] hover:bg-[#0f172a] text-white rounded-lg transition-all font-medium text-sm sm:text-base"
              >
                <Printer className="w-4 h-4" />
                Print
              </button>
            </div>
          </div>
        </div>

        {/* A4 Print Container */}
        <div className="flex justify-center py-4 sm:py-6 print:py-0 px-2 sm:px-4">
          <div
            ref={printRef}
            className="a4-page bg-white shadow-xl print:shadow-none w-full max-w-[210mm]"
          >
            <div className="px-3 sm:px-6 pt-4 sm:pt-6 pb-3 sm:pb-4 flex flex-col sm:flex-row justify-between items-start gap-3 sm:gap-0 border-b border-gray-200">
              <div className="flex items-center gap-3">
                <div>
                  <img
                    // src={MasterCompanyDetails?.[0]?.CompanyLogo || "/shipez-logo.png"}
                    src={"/shipez-logo.png"}
                    alt={
                      MasterCompanyDetails?.[0]?.CompanyName || "Company Logo"
                    }
                    className="h-10 sm:h-12 w-auto"
                  />
                  <p className="text-[10px] sm:text-xs text-gray-500">
                    Intelligent Freight Solutions
                  </p>
                </div>
              </div>
              <div className="text-left sm:text-right text-[10px] sm:text-xs text-gray-600">
                {MasterCompanyDetails?.[0]?.ProfileAddressPrint?.split(",").map(
                  (line, index) => (
                    <p key={index}>{line.trim()}</p>
                  ),
                )}
              </div>
            </div>
            {children}
          </div>
        </div>

        {/* Print Styles */}
        <style>
          {`
                    .a4-page {
                        width: 100%;
                        min-height: auto;
                        box-sizing: border-box;
                    }
                    
                    @media screen and (min-width: 768px) {
                        .a4-page {
                            width: 210mm;
                            min-height: 297mm;
                        }
                    }
                    
                    @media screen {
                        .a4-page {
                            margin: 0 auto;
                        }
                    }
                    
                    @media print {
                        @page {
                            size: A4;
                            margin: 10mm;
                        }
                        
                        html, body {
                            width: 210mm;
                            height: 297mm;
                        }
                        
                        body {
                            margin: 0;
                            padding: 0;
                            print-color-adjust: exact;
                            -webkit-print-color-adjust: exact;
                        }
                        
                        .a4-page {
                            width: 100%;
                            min-height: auto;
                            padding: 0;
                            margin: 0;
                            box-shadow: none !important;
                        }
                        
                        .print\\:hidden {
                            display: none !important;
                        }
                        
                        .print\\:py-0 {
                            padding-top: 0 !important;
                            padding-bottom: 0 !important;
                        }
                        
                        .print\\:shadow-none {
                            box-shadow: none !important;
                        }
                        
                        .print\\:bg-white {
                            background: white !important;
                        }
                    }
                `}
        </style>
      </div>
    </>
  );
}

export default PrintWrapper;
